import { FirebaseApp } from 'firebase/app';
import { collection, doc, getAggregateFromServer, query, sum, where, WhereFilterOp, writeBatch, getDocs } from '@firebase/firestore';
import { ModelService } from './ModelService';
import { SaleByProduct, SaleBy, SaleByTime } from '../../model/Sale';
import { ISaleService } from '../ISaleService';

export class SaleService extends ModelService<SaleByProduct> implements ISaleService {
  private salesByTimePath: string;
  private salesByFamilyPath: string;
  private salesByBrandPath: string;

  constructor(app: FirebaseApp, path: string) {
    super(app, path);
    this.salesByTimePath = this.path.replace(/\/sales(ByDate)?$/, '/salesByTime');
    this.salesByFamilyPath = this.path.replace(/\/sales(ByDate)?$/, '/salesByFamily');
    this.salesByBrandPath = this.path.replace(/\/sales(ByDate)?$/, '/salesByBrand');
  }

  async batchCreate(sales: SaleByProduct[]): Promise<void> {
    const batch = writeBatch(this.database);
    for(const sale of sales) {
      const ref = collection(this.database, this.path).withConverter(this.converter);
      batch.set(doc(ref), sale);
    }
    await batch.commit();
  }

  async batchCreateByTime(sales: SaleByTime[]): Promise<void> {
    const batch = writeBatch(this.database);
    for(const sale of sales) {
      const ref = collection(this.database, this.salesByTimePath);
      batch.set(doc(ref), sale);
    }
    await batch.commit();
  }

  async batchCreateByFamily(sales: SaleBy[]): Promise<void> {
    const batch = writeBatch(this.database);
    for(const sale of sales) {
      const ref = collection(this.database, this.salesByFamilyPath);
      batch.set(doc(ref), sale);
    }
    await batch.commit();
  }

  async batchCreateByBrand(sales: SaleBy[]): Promise<void> {
    const batch = writeBatch(this.database);
    for(const sale of sales) {
      const ref = collection(this.database, this.salesByBrandPath);
      batch.set(doc(ref), sale);
    }
    await batch.commit();
  }

  async getTotalAmount(conditions: { field: string, operator: string, value: any }[]): Promise<number> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, ...queries);
    const snapshot = await getAggregateFromServer(q, {
      totalAmount: sum('amount')
    });
    return snapshot.data().totalAmount || 0;
  }

  async getTotalUnits(conditions: { field: string, operator: string, value: any }[]): Promise<number> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, ...queries);
    const snapshot = await getAggregateFromServer(q, {
      totalUnits: sum('units')
    });
    return snapshot.data().totalUnits || 0;
  }

  async byTimeAndWhere(conditions: { field: string, operator: string, value: any }[]): Promise<SaleByTime[]> {
    const ref = collection(this.database, this.salesByTimePath);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, ...queries);
    const docs = await getDocs(q);
    return docs.docs.map(d => {
      const data = d.data();
      return {
        timestamp: data.timestamp,
        time: data.time,
        amount: data.amount,
        units: data.units
      }
    });
  }

  async byFamilyAndWhere(conditions: { field: string, operator: string, value: any }[]): Promise<SaleBy[]> {
    return await this.byGroupAndWhere(this.salesByFamilyPath, conditions);
  }

  async byBrandAndWhere(conditions: { field: string, operator: string, value: any }[]): Promise<SaleBy[]> {
    console.log(this.salesByBrandPath, conditions);
    return await this.byGroupAndWhere(this.salesByBrandPath, conditions);
  }

  private async byGroupAndWhere(path: string, conditions: { field: string, operator: string, value: any }[]): Promise<SaleBy[]> {
    const ref = collection(this.database, path);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, ...queries);
    const docs = await getDocs(q);
    return docs.docs.map(d => {
      const data = d.data();
      return {
        centerCode: data.centerCode,
        centerName: data.centerName,
        name: data.name,
        locationId: data.locationId,
        locationName: data.locationName,
        timestamp: data.timestamp,
        units: data.units,
        amount: data.amount
      }
    });
  }
}
